package com.example.crashbit

import androidx.compose.animation.animateColor
import androidx.compose.animation.core.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.clickable
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.ui.input.pointer.PointerEventType
import org.jetbrains.compose.resources.stringResource
import crashbit_multiplatform.composeapp.generated.resources.*
import kotlinx.coroutines.delay
import kotlin.math.roundToInt
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.PI
import kotlin.random.Random

data class InteractiveParticle(val pos: Offset, val timestamp: Long, val color: Color)

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun TitleScreen(viewModel: SpaceViewModel) {
    val scrollState = rememberScrollState()
    val uriHandler = LocalUriHandler.current
    var showAdvanced by remember { mutableStateOf(false) }
    var showChangelog by remember { mutableStateOf(false) }
    var showFullscreenToast by remember { mutableStateOf(false) }
    val interactiveParticles = remember { mutableStateListOf<InteractiveParticle>() }

    LaunchedEffect(showFullscreenToast) {
        if (showFullscreenToast) {
            delay(2500)
            showFullscreenToast = false
        }
    }

    LaunchedEffect(Unit) {
        while(true) {
            val now = currentTimeMillis()
            interactiveParticles.removeAll { now - it.timestamp > 2000 }
            kotlinx.coroutines.delay(100)
        }
    }

    val infiniteTransition = rememberInfiniteTransition(label = "pulse")
    val titleColor by infiniteTransition.animateColor(
        initialValue = Color.Yellow,
        targetValue = Color(0xFFFFD700).copy(alpha = 0.6f),
        animationSpec = infiniteRepeatable(
            animation = tween(2000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "titleColor"
    )

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
            .pointerInput(Unit) {
                awaitEachGesture {
                    while (true) {
                        val event = awaitPointerEvent()
                        val position = event.changes.first().position
                        
                        if (event.type == PointerEventType.Press || event.type == PointerEventType.Move) {
                            interactiveParticles.add(
                                InteractiveParticle(
                                    pos = position,
                                    timestamp = currentTimeMillis(),
                                    color = when(Random.nextInt(0, 3)) {
                                        0 -> Color.Cyan
                                        1 -> Color.Magenta
                                        else -> Color(0xFFFFD700)
                                    }
                                )
                            )
                        }
                        
                        if (event.changes.all { !it.pressed }) break
                    }
                }
            }
    ) {
        CelestialDanceBackground(scrollState.value, interactiveParticles)

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(32.dp)
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            val elementModifier = Modifier.widthIn(max = 600.dp)

            Text(
                text = stringResource(Res.string.app_name),
                color = titleColor,
                fontSize = 42.sp,
                lineHeight = 48.sp,
                fontWeight = FontWeight.ExtraBold,
                textAlign = TextAlign.Center,
                modifier = elementModifier
            )

            Text(
                text = if (!isSaveSupported) "Browser Version • iOS & Android Apps Coming Soon" else stringResource(Res.string.version_edition),
                color = if (!isSaveSupported) Color.Cyan.copy(alpha = 0.8f) else Color.White.copy(alpha = 0.5f),
                fontSize = 12.sp,
                fontWeight = if (!isSaveSupported) FontWeight.Bold else FontWeight.Normal,
                letterSpacing = if (!isSaveSupported) 1.sp else 4.sp,
                textAlign = TextAlign.Center,
                modifier = elementModifier
            )

            Spacer(modifier = Modifier.height(32.dp))

            MenuSectionHeader(stringResource(Res.string.header_core_missions), Color.Yellow, elementModifier)
            FlowRow(
                modifier = elementModifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalArrangement = Arrangement.spacedBy(12.dp),
                maxItemsInEachRow = 3
            ) {
                ModeButton(stringResource(Res.string.mode_single_star), Color.Yellow, Modifier.padding(horizontal = 4.dp).width(130.dp)) { viewModel.startNewGame(GameMode.SINGLE) }
                ModeButton(stringResource(Res.string.mode_dual_star), Color(0xFFFF9800), Modifier.padding(horizontal = 4.dp).width(130.dp)) { viewModel.startNewGame(GameMode.BINARY) }
                ModeButton(stringResource(Res.string.mode_crazy_eddie), Color(0xFFFF4500), Modifier.padding(horizontal = 4.dp).width(130.dp)) { viewModel.startNewGame(GameMode.CRAZY_EDDIE) }
            }

            MenuDivider(elementModifier)

            MenuSectionHeader(stringResource(Res.string.header_mission_control), Color.Cyan, elementModifier)
            if (viewModel.bodies.isNotEmpty() || (viewModel.stars.isNotEmpty() && !viewModel.isCustomPlacing)) {
                ModeButton(stringResource(Res.string.btn_resume_session), Color(0xFF03DAC5), elementModifier.width(280.dp)) { viewModel.resumeActiveSession() }
                Spacer(modifier = Modifier.height(12.dp))
            }
            if (viewModel.savedUniverses.isNotEmpty()) {
                ModeButton(stringResource(Res.string.btn_open_browser), Color.Cyan, elementModifier.width(280.dp)) { viewModel.navigateToBrowser() }
            } else {
                Text(stringResource(Res.string.label_no_saved_data), color = Color.White.copy(alpha = 0.3f), fontSize = 10.sp, fontWeight = FontWeight.Bold)
            }

            MenuDivider(elementModifier)

            MenuSectionHeader(stringResource(Res.string.header_command_intel), Color.White, elementModifier)
            val transition = rememberInfiniteTransition(label = "buttonGlow")
            val glowAlpha by transition.animateFloat(
                initialValue = 0.3f,
                targetValue = 1.0f,
                animationSpec = infiniteRepeatable(
                    animation = tween(600, easing = LinearEasing),
                    repeatMode = RepeatMode.Reverse
                ),
                label = "glowAlpha"
            )
            val uriHandler = LocalUriHandler.current
            val isLandscape = this@BoxWithConstraints.maxWidth > this@BoxWithConstraints.maxHeight
            val intelMaxItems = if (isLandscape) 4 else 2

            FlowRow(
                modifier = elementModifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalArrangement = Arrangement.spacedBy(10.dp),
                maxItemsInEachRow = intelMaxItems
            ) {
                ModeButton(stringResource(Res.string.btn_advanced), Color.Cyan, Modifier.padding(horizontal = 4.dp).width(130.dp)) { showAdvanced = true }
                ModeButton(stringResource(Res.string.btn_how_to_play), Color(0xFFBB86FC), Modifier.padding(horizontal = 4.dp).width(130.dp)) { viewModel.openAbout() }
                ModeButton(
                    text = stringResource(Res.string.btn_tutorial),
                    color = Color(0xFFA855F7),
                    border = BorderStroke(2.dp, Color.White.copy(alpha = glowAlpha)),
                    modifier = Modifier.padding(horizontal = 4.dp).width(130.dp)
                ) { viewModel.startTutorial(); viewModel.startNewGame(GameMode.SINGLE) }
                ModeButton(stringResource(Res.string.btn_watch_gameplay), Color(0xFFE53935), Modifier.padding(horizontal = 4.dp).width(148.dp)) {
                    uriHandler.openUri("https://youtu.be/ZYEdaMZsQ2k")
                }
                ModeButton(stringResource(Res.string.btn_changelog), Color(0xFF03DAC5), Modifier.padding(horizontal = 4.dp).width(130.dp)) {
                    showChangelog = true
                }
            }

            Spacer(modifier = Modifier.height(48.dp))
        }

        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(bottom = 12.dp)
                .padding(horizontal = 24.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
                modifier = Modifier.clickable { uriHandler.openUri("mailto:contact@crashbitfidget.com") }
            ) {
                Text(stringResource(Res.string.label_contact_questions) + " ", color = Color.White.copy(alpha = 0.5f), fontSize = 10.sp)
                Text(stringResource(Res.string.label_contact_email), color = Color.Cyan.copy(alpha = 0.8f), fontSize = 10.sp, fontWeight = FontWeight.Bold)
            }
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = stringResource(Res.string.label_photosensitivity_short),
                color = Color.White.copy(alpha = 0.3f),
                fontSize = 9.5.sp,
                textAlign = TextAlign.Center
            )
        }

        if (!isSaveSupported) {
            Button(
                onClick = {
                    toggleFullscreen()
                    showFullscreenToast = true
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color.Black.copy(alpha = 0.6f)),
                border = BorderStroke(1.dp, Color.Cyan.copy(alpha = 0.8f)),
                shape = RoundedCornerShape(20.dp),
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(16.dp)
            ) {
                Text(
                    text = stringResource(Res.string.btn_fullscreen),
                    color = Color.Cyan,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold
                )
            }
        }

        if (showFullscreenToast) {
            Surface(
                color = Color.Black.copy(alpha = 0.85f),
                shape = RoundedCornerShape(20.dp),
                border = BorderStroke(1.dp, Color.Cyan),
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .padding(bottom = 65.dp)
            ) {
                Text(
                    text = stringResource(Res.string.msg_fullscreen_hint),
                    color = Color.Cyan,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                )
            }
        }

        if (showAdvanced) {
            AdvancedOptionsOverlay(viewModel) { showAdvanced = false }
        }

        if (showChangelog) {
            ChangelogOverlay { showChangelog = false }
        }
    }
}

@Composable
fun AdvancedOptionsOverlay(viewModel: SpaceViewModel, onDismiss: () -> Unit) {
    Surface(
        modifier = Modifier.fillMaxSize(),
        color = Color.Black.copy(alpha = 0.92f)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(32.dp)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(stringResource(Res.string.title_advanced_config), color = Color.Cyan, fontSize = 24.sp, fontWeight = FontWeight.ExtraBold)
            Spacer(modifier = Modifier.height(24.dp))

            AdvancedToggle(stringResource(Res.string.label_show_performance), viewModel.isPerformanceMeterVisible) { viewModel.updateShowPerformance(it) }
            
            HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp).width(200.dp), color = Color.White.copy(alpha = 0.1f))

            Text(stringResource(Res.string.header_crazy_eddie_settings), color = Color.Yellow.copy(alpha = 0.6f), fontSize = 12.sp, fontWeight = FontWeight.Bold)
            Spacer(modifier = Modifier.height(12.dp))
            AdvancedToggle(stringResource(Res.string.label_stars_merge), viewModel.starsCanMerge) { viewModel.updateStarsCanMerge(it) }
            if (viewModel.starsCanMerge) {
                AdvancedSlider(stringResource(Res.string.label_merger_pass_limit), viewModel.mergerPassLimit.toFloat(), 1f..100f, Color.Yellow) { viewModel.updateMergerPassLimit(it.roundToInt()) }
                Text(stringResource(Res.string.format_passes).replace("%d", viewModel.mergerPassLimit.toString()), color = Color.White.copy(alpha = 0.5f), fontSize = 10.sp)
            }
            AdvancedToggle(stringResource(Res.string.label_stars_dynamic), viewModel.starsAreDynamic) { viewModel.updateStarsAreDynamic(it) }

            HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp).width(200.dp), color = Color.White.copy(alpha = 0.1f))

            Text(stringResource(Res.string.header_global_physics), color = Color.Cyan.copy(alpha = 0.6f), fontSize = 12.sp, fontWeight = FontWeight.Bold)
            Spacer(modifier = Modifier.height(12.dp))
            AdvancedToggle(stringResource(Res.string.label_stars_consume_planets), viewModel.planetsCanBeConsumed) { viewModel.updatePlanetsConsumed(it) }
            AdvancedSlider(stringResource(Res.string.label_boomerang_limit), viewModel.auLimit, 10f..20000f, Color.Cyan) { viewModel.updateAuLimit(it) }
            AdvancedSlider(stringResource(Res.string.label_biology_speed), viewModel.evolutionSpeed, 0.1f..10f, Color.Green) { viewModel.updateEvolutionSpeed(it) }

            HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp).width(200.dp), color = Color.White.copy(alpha = 0.1f))

            Text(stringResource(Res.string.header_time_mastery), color = Color(0xFFBB86FC).copy(alpha = 0.6f), fontSize = 12.sp, fontWeight = FontWeight.Bold)
            Spacer(modifier = Modifier.height(12.dp))
            AdvancedToggle(stringResource(Res.string.label_enable_rewind), viewModel.isRewindEnabled) { viewModel.updateRewindEnabled(it) }
            AdvancedToggle(stringResource(Res.string.label_deterministic_physics), viewModel.isDeterministicMode) { viewModel.updateDeterministicMode(it) }
            
            if (isHapticsSupported) {
                HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp).width(200.dp), color = Color.White.copy(alpha = 0.1f))

                Text(stringResource(Res.string.header_feedback_settings), color = Color.White.copy(alpha = 0.6f), fontSize = 12.sp, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(12.dp))
                AdvancedToggle(stringResource(Res.string.label_enable_haptics), viewModel.isVibrationEnabled) { viewModel.updateVibrationEnabled(it) }

                if (viewModel.isVibrationEnabled) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(stringResource(Res.string.label_intensity), color = Color.White.copy(alpha = 0.8f), fontSize = 11.sp, fontWeight = FontWeight.Bold)
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            IntensityButton(stringResource(Res.string.label_gentle), 0.1f, viewModel.hapticIntensity) { 
                                viewModel.updateHapticIntensity(0.1f)
                                viewModel.triggerTestHaptic(0.1f)
                            }
                            IntensityButton(stringResource(Res.string.label_medium), 0.5f, viewModel.hapticIntensity) { 
                                viewModel.updateHapticIntensity(0.5f)
                                viewModel.triggerTestHaptic(0.5f)
                            }
                            IntensityButton(stringResource(Res.string.label_stark), 1.0f, viewModel.hapticIntensity) { 
                                viewModel.updateHapticIntensity(1.0f)
                                viewModel.triggerTestHaptic(1.0f)
                            }
                        }
                    }
                }
            }

            if (isSaveSupported) {
                Spacer(modifier = Modifier.height(12.dp))
                AdvancedToggle(stringResource(Res.string.label_cloud_backup), viewModel.isCloudBackupEnabled) { viewModel.updateCloudBackupEnabled(it) }

                Spacer(modifier = Modifier.height(24.dp))

                Button(
                    onClick = { viewModel.currentScreen = Screen.Onboarding },
                    colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.2f)),
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.width(200.dp)
                ) {
                    Text(stringResource(Res.string.btn_change_language), fontWeight = FontWeight.Bold)
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            Button(
                onClick = { viewModel.resetToDefaults() },
                colors = ButtonDefaults.buttonColors(containerColor = Color.Red.copy(alpha = 0.7f)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text(stringResource(Res.string.btn_reset_defaults), fontWeight = FontWeight.Bold)
            }

            Spacer(modifier = Modifier.height(16.dp))

            Button(
                onClick = onDismiss,
                colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.5f)),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.width(200.dp)
            ) {
                Text(stringResource(Res.string.btn_back_to_command), fontWeight = FontWeight.Bold)
            }
        }
    }
}

@Composable
fun AdvancedToggle(label: String, checked: Boolean, onCheckedChange: (Boolean) -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(label, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold)
        Switch(
            checked = checked, 
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color.Cyan,
                checkedTrackColor = Color.Cyan.copy(alpha = 0.4f)
            )
        )
    }
}

@Composable
fun AdvancedSlider(label: String, value: Float, range: ClosedFloatingPointRange<Float>, color: Color, onValueChange: (Float) -> Unit) {
    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)) {
        Text("$label: ${value.roundToInt()}", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold)
        Slider(
            value = value,
            onValueChange = onValueChange,
            valueRange = range,
            colors = SliderDefaults.colors(thumbColor = color, activeTrackColor = color)
        )
    }
}

@Composable
fun IntensityButton(label: String, level: Float, current: Float, onClick: () -> Unit) {
    val selected = level == current
    OutlinedButton(
        onClick = onClick,
        modifier = Modifier.height(28.dp),
        contentPadding = PaddingValues(horizontal = 8.dp),
        shape = RoundedCornerShape(14.dp),
        colors = ButtonDefaults.outlinedButtonColors(
            containerColor = if (selected) Color.White.copy(alpha = 0.2f) else Color.Transparent,
            contentColor = if (selected) Color.Cyan else Color.White.copy(alpha = 0.6f)
        ),
        border = androidx.compose.foundation.BorderStroke(1.dp, if (selected) Color.Cyan else Color.White.copy(alpha = 0.2f))
    ) {
        Text(label, fontSize = 9.sp, fontWeight = FontWeight.Bold)
    }
}

@Composable
fun ModeButton(text: String, color: Color, modifier: Modifier = Modifier, border: BorderStroke? = null, onClick: () -> Unit) {
    Button(
        onClick = onClick,
        colors = ButtonDefaults.buttonColors(
            containerColor = color,
            contentColor = Color.Black
        ),
        shape = RoundedCornerShape(8.dp),
        border = border,
        modifier = modifier.height(38.dp)
    ) {
        Text(text = text, fontWeight = FontWeight.Bold, fontSize = 11.sp, maxLines = 1, textAlign = TextAlign.Center)
    }
}

@Composable
fun ChangelogOverlay(onDismiss: () -> Unit) {
    Surface(
        modifier = Modifier.fillMaxSize(),
        color = Color.Black.copy(alpha = 0.94f)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                stringResource(Res.string.title_changelog),
                color = Color.Cyan,
                fontSize = 20.sp,
                fontWeight = FontWeight.ExtraBold
            )
            Spacer(modifier = Modifier.height(20.dp))

            defaultChangelogEntries.forEach { entry ->
                Surface(
                    color = Color.White.copy(alpha = 0.05f),
                    shape = RoundedCornerShape(12.dp),
                    border = BorderStroke(1.dp, Color.Cyan.copy(alpha = 0.3f)),
                    modifier = Modifier.fillMaxWidth().widthIn(max = 500.dp).padding(vertical = 8.dp)
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(entry.version, color = Color.Cyan, fontSize = 14.sp, fontWeight = FontWeight.ExtraBold)
                            Text(entry.date, color = Color.White.copy(alpha = 0.5f), fontSize = 11.sp, fontWeight = FontWeight.Bold)
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                        entry.highlights.forEach { item ->
                            Text("• $item", color = Color.White.copy(alpha = 0.85f), fontSize = 11.sp, lineHeight = 15.sp, modifier = Modifier.padding(vertical = 2.dp))
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            Button(
                onClick = onDismiss,
                colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.5f)),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.width(180.dp)
            ) {
                Text(stringResource(Res.string.btn_back_to_command), fontWeight = FontWeight.Bold)
            }
        }
    }
}

@Composable
fun CelestialDanceBackground(scrollOffset: Int, interactiveParticles: List<InteractiveParticle>) {
    val time = rememberInfiniteTransition(label = "dance").animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec = infiniteRepeatable(
            animation = tween(20000, easing = LinearEasing),
            repeatMode = RepeatMode.Restart
        ),
        label = "time"
    )

    val backgroundBodies = remember {
        List(60) {
            val color = when(Random.nextInt(0, 3)) {
                0 -> Color.Cyan
                1 -> Color.Magenta
                else -> Color(0xFFFFD700)
            }
            Triple(
                Offset((Random.nextFloat() * 2000f), (Random.nextFloat() * 2000f)),
                (Random.nextFloat() * 0.5f + 0.5f),
                color
            )
        }
    }

    Canvas(modifier = Modifier.fillMaxSize()) {
        val parallaxOffset = scrollOffset * 0.2f
        val centerX = size.width / 2f
        val centerY = size.height / 2f - parallaxOffset

        backgroundBodies.forEach { (settings, speed, color) ->
            val angle = ((time.value * speed + settings.y) * PI / 180.0).toFloat()
            val radius = settings.x
            
            val x = centerX + cos(angle.toDouble()).toFloat() * radius
            val y = centerY + sin(angle.toDouble()).toFloat() * radius
            
            drawCircle(
                color = color.copy(alpha = 0.8f),
                radius = 3.5f,
                center = Offset(x, y)
            )
        }

        interactiveParticles.forEach { particle ->
            val age = (currentTimeMillis() - particle.timestamp).toFloat()
            val fade = (1.0f - (age / 2000f)).coerceIn(0f, 1f)
            
            if (fade > 0f) {
                drawCircle(
                    brush = Brush.radialGradient(
                        colors = listOf(particle.color.copy(alpha = fade * 0.5f), Color.Transparent),
                        center = particle.pos,
                        radius = 30f
                    ),
                    radius = 30f,
                    center = particle.pos
                )
                drawCircle(
                    color = particle.color.copy(alpha = fade),
                    radius = 2f,
                    center = particle.pos
                )
            }
        }
        
        drawRect(
            color = Color.Black.copy(alpha = 0.4f),
            size = size
        )
    }
}

@Composable
fun MenuSectionHeader(text: String, color: Color, modifier: Modifier = Modifier) {
    Text(
        text = text,
        color = color.copy(alpha = 0.5f),
        fontSize = 11.sp,
        fontWeight = FontWeight.ExtraBold,
        letterSpacing = 2.sp,
        modifier = modifier.padding(bottom = 12.dp)
    )
}

@Composable
fun MenuDivider(modifier: Modifier = Modifier) {
    HorizontalDivider(
        modifier = modifier.padding(vertical = 24.dp).width(100.dp),
        color = Color.White.copy(alpha = 0.1f),
        thickness = 1.dp
    )
}
